import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';

import {
  AboutPageDefaultContentTemplateDynamicZone,
  ComponentCommonDownloadableContentList,
  ComponentCommonTextEditor,
  InsightDefaultContentTemplateDynamicZone,
  Maybe,
  PodcastDefaultContentTemplateDynamicZone,
  PodcastEntity,
  PodcastListeningTimeEntity,
  ResourceDefaultContentTemplateDynamicZone,
  ResourceEntity,
} from '@/graphql/generated/graphql';

import DefaultContentTemplateSkeleton from './DefaultContentTemplate.skeleton';
import DefaultContentTemplateBullets from './Bullets';
import DefaultContentTemplateQuote from './Quote';
import DefaultContentTemplateFaq from './Faq';
import DefaultContentTemplateAuthorDetailed from './AuthorDetailed';
import DefaultContentTemplateTags from './Tags';
import DefaultContentTemplateRelatedContent, { RelatedContent } from './RelatedContent';
import Divider from '../Divider';

import DefaultContentTemplateH3 from './H3';
import DefaultContentTemplateTitle from './Title';
import DefaultContentTemplateTextEditor from './TextEditor';
import DefaultContentTemplateMedia from './Media';
import DefaultContentTemplateAuthor from './Author';
import DefaultContentTemplateEmbedded from './Embedded';
import DefaultContentTemplateDownloadableContent from './DownloadableContent';

import './index.css';
import DefaultContentTemplatePartnership from './Partnership';
import DefaultContentTemplateAudio from './Audio';
import { getAuthors } from '@/services/knowledge-hub/utils';
export interface ComponentCommonTextEditorWithHtml extends ComponentCommonTextEditor {
  markdownHtml: string;
}

export type ContentProps =
  | Array<Maybe<InsightDefaultContentTemplateDynamicZone>>
  | Array<Maybe<ResourceDefaultContentTemplateDynamicZone>>
  | Array<Maybe<PodcastDefaultContentTemplateDynamicZone>>
  | Array<Maybe<AboutPageDefaultContentTemplateDynamicZone>>
  | null;

type DefaultContentTemplateProps = {
  loading?: boolean;
  content?: ContentProps;
  tags?: string[];
  relatedContent?: RelatedContent[];
  fitContent?: boolean;
  handleTimeUpdate?: (audio: HTMLAudioElement) => void;
  listeningTime?: PodcastListeningTimeEntity;
  idBodyDivider?: boolean;
  downloadableContentTitle?: string;
  onPlayAudio?: () => void;
  onVideoPlay?: () => void;
  meditationCard?: {
    image: string;
  };
};

function DefaultContentTemplate({
  content = [],
  loading,
  tags,
  relatedContent,
  fitContent,
  listeningTime,
  handleTimeUpdate,
  downloadableContentTitle,
  onPlayAudio,
  onVideoPlay,
  meditationCard,
}: DefaultContentTemplateProps) {
  if (loading) {
    return <DefaultContentTemplateSkeleton />;
  }

  if (content?.length === 0) {
    return <h3 className="my-sh-30 font-larken text-sh-24">No content available</h3>;
  }

  return (
    <div className="default-content-template no-last-gap flex flex-col gap-y-sh-30 !font-aptos">
      {content?.map((c) => {
        if (c?.__typename === 'ComponentCommonDivider') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <Divider key={c.id} />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonH3') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateH3 key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonTitle') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateTitle key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonTextEditor') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateTextEditor
                  key={c.id}
                  component={c as ComponentCommonTextEditorWithHtml}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonDownloadableContentList') {
          return (
            <>
              <DefaultContentTemplateDownloadableContent
                key={c.id}
                component={c as ComponentCommonDownloadableContentList}
                title={downloadableContentTitle}
                isDefaultContentTemplate
              />
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonMedia') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateMedia
                  key={c.id}
                  component={c}
                  className={fitContent ? '' : 'lg:-mx-[12%]'}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (
          c?.__typename === 'ComponentCommonAuthor' &&
          (!!c?.publicationDate || !!c?.author?.data?.attributes?.name)
        ) {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateAuthor key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonEmbedded') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateEmbedded
                  onVideoPlay={onVideoPlay}
                  key={c.id}
                  component={c}
                  fitContent
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonBullet') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateBullets key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonQuote') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateQuote key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonFaqComponent') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateFaq key={c.id} component={c} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonAuthorDetailed') {
          return (
            <>
              <Divider key={`divider-${c.id}`} />
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateAuthorDetailed key={c.id} component={c} />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonTags') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateTags key={c.id} tags={tags || []} isDefaultContentTemplate />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonRelatedContent') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateRelatedContent
                  key={c.id}
                  relatedContent={relatedContent || []}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentKnowledgeHubResourcesRelated') {
          const relatedContentResource = (c?.resources?.data || []).map((resource) => {
            return {
              title: resource.attributes?.title || '',
              description: resource.attributes?.briefDescription || '',
              author: getAuthors(resource as ResourceEntity),
              link: `/knowledge-hub/slug/${resource.attributes?.slug}`,
              type: resource.attributes?.resourceType?.data?.attributes?.type?.toString() || '',
              image: {
                url: resource.attributes?.cover?.data?.attributes?.url || '',
                alternativeText: resource.attributes?.cover?.data?.attributes?.alternativeText || '',
              },
            };
          });

          const relatedContentPodcast = (c?.podcasts?.data || []).map((podcast) => {
            return {
              title: podcast.attributes?.title || '',
              description: podcast.attributes?.briefDescription || '',
              author: getAuthors(podcast as PodcastEntity),
              link: `/podcast/${podcast.attributes?.slug}`,
              type: 'Podcast',
              image: {
                url: podcast.attributes?.cover?.data?.attributes?.url || '',
                alternativeText: podcast.attributes?.cover?.data?.attributes?.alternativeText || '',
              },
            };
          });

          const relatedContentMeditation = (c?.exercises?.data || []).map((exercise) => {
            return {
              title: exercise.attributes?.title || '',
              description: exercise.attributes?.briefDescription || '',
              author: exercise?.attributes?.presentedBy || '',
              link: `/mindful-exercise/${exercise.attributes?.slug}`,
              type: exercise?.attributes?.mindful_exercise_type?.data?.attributes?.name || 'Mindful exercise',
              image: {
                url: meditationCard?.image || '',
                alternativeText: '',
              },
              noImage: !meditationCard?.image,
            };
          });

          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateRelatedContent
                  key={c.id}
                  relatedContent={[...relatedContentResource, ...relatedContentPodcast, ...relatedContentMeditation]}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonInPartnershipWith') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplatePartnership
                  key={c.id}
                  partnership={c.partnership || ''}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        if (c?.__typename === 'ComponentCommonAudio') {
          return (
            <>
              <AnimatedFadeInComponent key={c.id}>
                <DefaultContentTemplateAudio
                  key={c.id}
                  audio={c}
                  onPlayAudio={onPlayAudio}
                  handleTimeUpdate={handleTimeUpdate}
                  listeningTime={listeningTime}
                  isDefaultContentTemplate
                />
              </AnimatedFadeInComponent>
            </>
          );
        }

        return null;
      })}
    </div>
  );
}

export default DefaultContentTemplate;
